import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { API } from '../utils/apiConfig';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Repeat, CalendarOff, Loader2 } from 'lucide-react';

const WEEKDAYS = [
  { v: 0, l: 'Monday' }, { v: 1, l: 'Tuesday' }, { v: 2, l: 'Wednesday' },
  { v: 3, l: 'Thursday' }, { v: 4, l: 'Friday' }, { v: 5, l: 'Saturday' }, { v: 6, l: 'Sunday' },
];
const PATTERNS = [
  { v: '1', l: '1st' }, { v: '2', l: '2nd' }, { v: '3', l: '3rd' }, { v: '4', l: '4th' },
  { v: 'last', l: 'Last' }, { v: 'odd', l: 'Odd week' }, { v: 'even', l: 'Even week' },
];
const REASONS = ['Holiday', 'Maintenance', 'Tournament', 'Private Booking', 'Other'];

const emptyRule = {
  scope: 'court', venue_id: '', court_id: '', weekday: 5, patterns: [],
  action: 'closed', open_time: '06:00', close_time: '14:00',
  start_date: '', end_date: '', notes: '', is_active: true,
};
const emptyOverride = {
  scope: 'court', venue_id: '', court_id: '', start_date: '', end_date: '',
  reason: 'Holiday', action: 'closed', open_time: '06:00', close_time: '14:00',
  notes: '', is_active: true,
};

export const AvailabilityManager = ({ mode, venues }) => {
  const isRule = mode === 'recurring';
  const endpoint = isRule ? 'recurring-rules' : 'date-overrides';
  const itemsKey = isRule ? 'rules' : 'overrides';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(isRule ? emptyRule : emptyOverride);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/availability/${endpoint}`);
      setItems(res.data[itemsKey] || []);
    } catch (e) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [endpoint, itemsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const venueCourts = (venueId) => {
    const v = venues.find((x) => x.id === venueId);
    return v ? (v.courts || []) : [];
  };

  const openCreate = () => {
    setEditing(null);
    setForm(isRule ? { ...emptyRule } : { ...emptyOverride });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm(isRule
      ? { ...emptyRule, ...it, patterns: it.patterns || [] }
      : { ...emptyOverride, ...it });
    setDialogOpen(true);
  };
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const togglePattern = (p) => setForm((prev) => ({
    ...prev,
    patterns: prev.patterns.includes(p) ? prev.patterns.filter((x) => x !== p) : [...prev.patterns, p],
  }));

  const handleSave = async () => {
    if (!form.venue_id) return toast.error('Select a venue');
    if (form.scope === 'court' && !form.court_id) return toast.error("Select a court or choose 'All courts'");
    if (!form.start_date) return toast.error('Start date is required');
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.scope === 'venue') payload.court_id = null;
      if (editing) {
        await axios.put(`${API}/availability/${endpoint}/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/availability/${endpoint}`, payload);
        toast.success('Created');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/availability/${endpoint}/${it.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const actionBadge = (a) => ({
    closed: 'bg-red-100 text-red-700',
    custom: 'bg-amber-100 text-amber-700',
    open: 'bg-emerald-100 text-emerald-700',
  }[a] || 'bg-slate-100 text-slate-600');

  const patternLabel = (p) => (PATTERNS.find((x) => x.v === p)?.l || p);

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-800">
          {isRule
            ? 'Recurring rules override the weekly schedule (e.g. close every 2nd & 4th Saturday, or half-day on 1st & 3rd Saturday). Priority: Date Overrides > Recurring Rules > Weekly schedule.'
            : 'Date overrides are the highest priority (holidays, events, one-off closures or custom hours) and beat recurring rules and the weekly schedule.'}
        </p>
      </Card>

      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{items.length} {isRule ? 'rule(s)' : 'override(s)'}</p>
        <Button onClick={openCreate} className="gap-2" data-testid={`add-${mode}-btn`}>
          <Plus className="w-4 h-4" /> {isRule ? 'Add Rule' : 'Add Override'}
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-blue-500" /></Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">
          {isRule ? <Repeat className="w-9 h-9 mx-auto mb-2 text-slate-300" /> : <CalendarOff className="w-9 h-9 mx-auto mb-2 text-slate-300" />}
          No {isRule ? 'recurring rules' : 'date overrides'} yet
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Card key={it.id} className="p-4 flex items-start justify-between gap-3" data-testid={`${mode}-item-${it.id}`}>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-800">{it.venue_name}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-sm text-slate-600">{it.court_id ? it.court_name : 'All Courts'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionBadge(it.action)}`}>
                    {it.action === 'custom' ? `Custom ${it.open_time}–${it.close_time}` : it.action.charAt(0).toUpperCase() + it.action.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {isRule ? (
                    <>{WEEKDAYS.find((w) => w.v === it.weekday)?.l}
                      {it.patterns?.length ? ` · ${it.patterns.map(patternLabel).join(', ')}` : ' · Every week'}</>
                  ) : (
                    <>{it.reason} · {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` → ${it.end_date}` : ''}</>
                  )}
                  {isRule && <> · from {it.start_date}{it.end_date ? ` to ${it.end_date}` : ''}</>}
                </p>
                {it.notes && <p className="text-xs text-slate-400 mt-0.5">{it.notes}</p>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-${mode}-${it.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(it)} data-testid={`delete-${mode}-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} {isRule ? 'Recurring Rule' : 'Date Override'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Venue</Label>
                <Select value={form.venue_id} onValueChange={(v) => setForm((p) => ({ ...p, venue_id: v, court_id: '' }))}>
                  <SelectTrigger data-testid={`${mode}-venue`}><SelectValue placeholder="Select venue" /></SelectTrigger>
                  <SelectContent>{venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Apply To</Label>
                <Select value={form.scope} onValueChange={(v) => setField('scope', v)}>
                  <SelectTrigger data-testid={`${mode}-scope`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="court">Specific Court</SelectItem>
                    <SelectItem value="venue">All Courts (venue)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.scope === 'court' && (
              <div className="space-y-1.5">
                <Label>Court</Label>
                <Select value={form.court_id || ''} onValueChange={(v) => setField('court_id', v)} disabled={!form.venue_id}>
                  <SelectTrigger data-testid={`${mode}-court`}><SelectValue placeholder="Select court" /></SelectTrigger>
                  <SelectContent>{venueCourts(form.venue_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {isRule ? (
              <>
                <div className="space-y-1.5">
                  <Label>Weekday</Label>
                  <Select value={String(form.weekday)} onValueChange={(v) => setField('weekday', parseInt(v, 10))}>
                    <SelectTrigger data-testid="rule-weekday"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEKDAYS.map((w) => <SelectItem key={w.v} value={String(w.v)}>{w.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Week Pattern <span className="text-slate-400 font-normal">(none = every week)</span></Label>
                  <div className="flex flex-wrap gap-2">
                    {PATTERNS.map((p) => (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => togglePattern(p.v)}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${form.patterns.includes(p.v) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                        data-testid={`rule-pattern-${p.v}`}
                      >{p.l}</button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label>Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setField('reason', v)}>
                  <SelectTrigger data-testid="override-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => setField('action', v)}>
                <SelectTrigger data-testid={`${mode}-action`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="custom">Custom Hours</SelectItem>
                  {isRule && <SelectItem value="open">Open (force open)</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {form.action === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Open Time</Label>
                  <Input type="time" value={form.open_time} onChange={(e) => setField('open_time', e.target.value)} data-testid={`${mode}-open-time`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Close Time</Label>
                  <Input type="time" value={form.close_time} onChange={(e) => setField('close_time', e.target.value)} data-testid={`${mode}-close-time`} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} data-testid={`${mode}-start-date`} />
              </div>
              <div className="space-y-1.5">
                <Label>End Date <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input type="date" value={form.end_date || ''} onChange={(e) => setField('end_date', e.target.value)} data-testid={`${mode}-end-date`} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid={`${mode}-notes`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid={`save-${mode}-btn`}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (editing ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
